import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, TextField, Grid, Table, TableBody, TableCell, TableHead, TableRow } from "@mui/material";
import { useState } from "react";
import BasicDatePicker from "../../../../../../../../components/BasicDatePicker";
import moment, { Moment } from "moment";
import ConfirmDialog from "../../../../../../../../components/BasicComponents/Dialogs/ConfirmDialog";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { useMutation, useQueryClient } from "react-query";
import axiosClient from "../../../../../../../../lib/axios";
import { enqueueSnackbar } from "notistack";
import SelectPublico from "../../../../../../../../components/SelectsSimpleApiQuery/SelectPublico";
import { IDataPublicos } from "../../../../../../../../types/api/geral/pesquisar-publicos";
import { ISolicitarCotacaoParams } from "../../../../../../../../types/mutations/estoque/enviar-cotacao-fornecedor";
import { ItemSolicitacao } from "../..";
import { desformatarTelefone } from "../../../../../../../../helpers/unmask";
import { formatarTelefone } from "../../../../../../../../helpers/masks";
import { QuantityInput } from "../../../../../../../../components/BaseInputs/QuantityInput";

export default function DialogCotacaoFornecedor({
    solicitacaoId,
    open,
    itens,
    onClose
}: DialogCotacaoFornecedorProps) {

    const queryClient = useQueryClient();
    const [openModalConfirmacao, setOpenModalConfirmacao] = useState(false);

    const { mutate: solicitarCotacao, isLoading } = useMutation({
        mutationKey: 'solicitar-cotacao-fornecedor',
        mutationFn: (inputs: ISolicitarCotacaoParams) => axiosClient.post(`/estoque/compras/enviar-cotacao-fornecedor/${solicitacaoId}`, inputs),
        onError: (error) => {
            console.error(error)
            enqueueSnackbar('Erro ao solicitar cotação!', { variant: 'error' });
        },
        onSuccess: () => {
            enqueueSnackbar(`Solicitação de cotação enviada com sucesso!`, { variant: "success" });
            queryClient.invalidateQueries('detalhe-solicitacao-compra')
            onClose()
        }
    })

    const methods = useForm<TSolicitarCotacaoInputs>({
        defaultValues: {
            fornecedor: null,
            email: "",
            vencimento: moment(),
            produtos: itens.map(item => ({
                produto_id: item.produto_id,
                nome: item.nome,
                quantidade: item.quantidade,
                quantidade_alterada: item.quantidade,
                unidade_medida: item.unidade_medida,
                observacoes: item.observacoes
            })),
            telefone: ""
        }
    });

    const { control, handleSubmit, watch } = methods;
    const produtosSelecionados = watch("produtos");

    const onSubmit = (data: TSolicitarCotacaoInputs) => {
        if (!data.fornecedor) {
            enqueueSnackbar("Fornecedor é obrigatório", { variant: "error" });
            return;
        }
        if (!data.email) {
            enqueueSnackbar("Email é obrigatório", { variant: "error" });
            return;
        }

        const params: ISolicitarCotacaoParams = {
            fornecedor_id: Number(data.fornecedor.codigo_publico),
            tipo_publico: data.fornecedor.tipo_publico,
            email: data.email,
            vencimento: data.vencimento ? data.vencimento.format("YYYY-MM-DD") : "",
            produtos: data.produtos.map(produto => ({
                produto_id: produto.produto_id,
                quantidade: produto.quantidade,
                quantidade_alterada: produto.quantidade_alterada
            })),
            telefone: data.telefone ? desformatarTelefone(data.telefone) : null
        };
        solicitarCotacao(params);
    };

    return (
        <FormProvider {...methods}>
            <Dialog
                open={open}
                onClose={onClose}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>Solicitar Cotação de Fornecedor</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ mb: 3 }}>
                        Preencha os dados do fornecedor para enviar uma solicitação de cotação.
                        O fornecedor receberá um link por email para preencher os valores.
                    </DialogContentText>

                    <Grid container spacing={3} sx={{ mb: 3 }}>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <Controller
                                name="fornecedor"
                                rules={{ required: "Fornecedor é obrigatório" }}
                                control={control}
                                render={({ field, fieldState: { error, invalid } }) => (
                                    <SelectPublico
                                        label="Fornecedor"
                                        value={field.value as IDataPublicos}
                                        onChange={(value) => {
                                            field.onChange(value)
                                            if (value && value.email) {
                                                methods.setValue("email", value.email);
                                            }
                                        }}
                                        required
                                        error={invalid}
                                        errorMessage={error?.message}
                                        defaultFiltroTipoClient={['F']}
                                        showAllTypes
                                    />
                                )}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <Controller
                                name="email"
                                control={control}
                                rules={{
                                    required: "Email é obrigatório",
                                    pattern: {
                                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                        message: "Email inválido"
                                    }
                                }}
                                render={({ field, fieldState: { error, invalid } }) => (
                                    <TextField
                                        fullWidth
                                        label="Email"
                                        type="email"
                                        value={field.value}
                                        onChange={field.onChange}
                                        required
                                        error={invalid}
                                        helperText={error?.message}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <Controller
                                name="telefone"
                                control={control}
                                rules={{
                                    required: "Telefone é obrigatório",
                                    pattern: {
                                        value: /^\(\d{2}\) \d{4,5}-\d{4}$/,
                                        message: "Telefone inválido"
                                    }
                                }}
                                render={({ field, fieldState: { error, invalid } }) => (
                                    <TextField
                                        fullWidth
                                        required
                                        label="Whatsapp/Telefone"
                                        type="text"
                                        value={field.value}
                                        onChange={(e) => field.onChange(formatarTelefone(e.target.value))}
                                        error={invalid}
                                        helperText={error?.message}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <Controller
                                name="vencimento"
                                control={control}
                                rules={{
                                    required: "Vencimento da solicitação é obrigatório",
                                    validate: (value) => {
                                        if (value && value.isBefore(moment(), 'day')) {
                                            return "A data de vencimento não pode ser anterior a hoje";
                                        }
                                    }
                                }}
                                render={({ field, fieldState: { error, invalid } }) => (
                                    <BasicDatePicker
                                        required
                                        label="Vencimento da solicitação"
                                        blockPastDates
                                        value={field.value}
                                        onChange={(data) => field.onChange(data)}
                                        formatString="DD/MM/YYYY"
                                        error={invalid}
                                        helperText={error?.message}
                                    />
                                )}
                            />
                        </Grid>
                    </Grid>

                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell><b>Produto</b></TableCell>
                                <TableCell align="center"><b>Quantidade</b></TableCell>
                                <TableCell><b>Unidade</b></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {produtosSelecionados.map((produto, index) => (
                                <TableRow key={index}>
                                    <TableCell>
                                        <div>
                                            <div style={{ fontWeight: 'bold' }}>{produto.produto_id}</div>
                                            <div>{produto.nome}</div>
                                        </div>
                                    </TableCell>
                                    <TableCell align="center">
                                        <Controller
                                            name={`produtos.${index}.quantidade_alterada`}
                                            control={control}
                                            render={({ field }) => (
                                                <QuantityInput
                                                    width={'100px'}
                                                    value={`${field.value}`}
                                                    onChange={(newValue) => {
                                                        field.onChange(newValue);
                                                    }}
                                                />
                                            )}
                                        />
                                    </TableCell>
                                    <TableCell>{produto.unidade_medida}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose}>Cancelar</Button>
                    <Button
                        variant="contained"
                        color="primary"
                        disabled={isLoading}
                        onClick={() => setOpenModalConfirmacao(true)}>
                        {isLoading ? 'Enviando...' : 'Solicitar'}
                    </Button>
                </DialogActions>
            </Dialog>

            {openModalConfirmacao && (
                <ConfirmDialog
                    title="Solicitar cotação"
                    message="Deseja enviar o formulário de solicitação de cotação para o fornecedor selecionado?"
                    cancelText="Cancelar"
                    confirmText="Enviar"
                    confirmButtonColor="primary"
                    open={openModalConfirmacao}
                    onClose={() => setOpenModalConfirmacao(false)}
                    onConfirm={handleSubmit(onSubmit)}
                    disabled={isLoading}
                />
            )}
        </FormProvider>
    )
}

interface DialogCotacaoFornecedorProps {
    solicitacaoId: number;
    open: boolean;
    itens: ItemSolicitacao[];
    onClose: () => void;
}

type TSolicitarCotacaoInputs = {
    fornecedor: IDataPublicos | null
    email: string
    vencimento: Moment | null
    produtos: Array<{
        produto_id: number
        nome: string
        quantidade: number
        quantidade_alterada: number
        unidade_medida: string
        observacoes: string
    }>
    telefone: string
}